import instance, { ADMIN_PREFIX } from 'services/api';
import {
  AttachDoctorToServicesInput,
  DoctorListQuery,
  DoctorListResponse,
  DoctorListRow,
  DoctorProfile,
  DoctorUpdateInput
} from 'types/api/doctors';

/**
 * GET /api/v2/admin/doctors — paginated list with status/search filters
 * and org-scoping via the organization header. BE returns the legacy
 * {results,total} envelope; first_name/last_name @Lang() locale-picked.
 */
export const fetchDoctors = async (
  query: DoctorListQuery
): Promise<DoctorListResponse> => {
  const params: Record<string, string | number> = {
    page: query.page ?? 1,
    limit: query.limit ?? 10
  };

  if (query.status) params.status = query.status;
  if (query.name) params.search = query.name;
  if (query.search) params.search = query.search;

  const res = await instance.get<DoctorListResponse>(
    `${ADMIN_PREFIX}/doctors`,
    {
      params
    }
  );

  return {
    results: res.data.results ?? [],
    total: res.data.total ?? 0
  };
};

/**
 * GET /api/v2/admin/doctors/:id?lang= — detail with specialties /
 * service / tags / plans / groups relations hydrated (cycle-21).
 */
export const fetchDoctorById = async (
  id: string | number,
  lang?: string
): Promise<DoctorProfile> => {
  const res = await instance.get<DoctorProfile>(
    `${ADMIN_PREFIX}/doctors/${id}`,
    lang ? { params: { lang } } : undefined
  );

  return res.data;
};

/**
 * GET /api/v2/admin/doctors/by-organization/:id — all doctors attached
 * to an organization. Returns bare array.
 */
export const fetchDoctorsByOrganization = async (
  organizationId: string | number
): Promise<DoctorListRow[]> => {
  const res = await instance.get<DoctorListRow[]>(
    `${ADMIN_PREFIX}/doctors/by-organization/${organizationId}`
  );

  return res.data ?? [];
};

/**
 * PUT /api/v2/admin/doctors/:id — admin doctor profile update
 * (cycle-22). jsonb fields accept per-locale string + `lang` for the
 * merge path, OR a full Record for full replace. UPDATE_WORKING_HOURS
 * folds onto this endpoint via `{ working_hours }`.
 */
export const updateDoctor = (
  id: string | number,
  input: DoctorUpdateInput
): Promise<unknown> => instance.put(`${ADMIN_PREFIX}/doctors/${id}`, input);

/**
 * DELETE /api/v2/admin/doctors/:id — soft delete (sets removed=true).
 */
export const deleteDoctor = (id: string | number): Promise<unknown> =>
  instance.delete(`${ADMIN_PREFIX}/doctors/${id}`);

/**
 * POST /api/v2/admin/doctors-services/attach-doctor — replace this
 * doctor's service-link set with the provided service_ids (P14 SHAPE 1).
 */
export const attachDoctorToServices = (
  input: AttachDoctorToServicesInput
): Promise<unknown> =>
  instance.post(`${ADMIN_PREFIX}/doctors-services/attach-doctor`, input);
